/**
 * Plan Context
 * Manages global plan and month_plan state
 * Task 002 - Plans Management
 */

import React, { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback } from 'react';
import {
  Plan,
  PlanWithStats,
  MonthPlan,
  CreatePlanData,
  UpdatePlanData,
  CreateMonthPlanData,
  PlanContextType,
} from '../types/plan';
import * as planService from '../services/planService';
import { useAuth } from './AuthContext';

const PlanContext = createContext<PlanContextType | undefined>(undefined);

interface PlanProviderProps {
  children: ReactNode;
}

export const PlanProvider: React.FC<PlanProviderProps> = ({ children }) => {
  const [plans, setPlans] = useState<PlanWithStats[]>([]);
  const [currentPlan, setCurrentPlan] = useState<Plan | null>(null);
  const [currentMonthPlan, setCurrentMonthPlan] = useState<MonthPlan | null>(null);
  const [allMonthPlans, setAllMonthPlans] = useState<MonthPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { session, loading: authLoading } = useAuth();
  const currentPlanIdRef = useRef<string | null>(null);

  useEffect(() => {
    currentPlanIdRef.current = currentPlan?.id ?? null;
  }, [currentPlan?.id]);

  const resetState = useCallback(() => {
    setPlans([]);
    setCurrentPlan(null);
    setCurrentMonthPlan(null);
    setAllMonthPlans([]);
    setError(null);
    setLoading(false);
  }, []);

  /**
   * React to auth session changes
   */
  const loadPlans = useCallback(async () => {
    if (!session) {
      resetState();
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const userPlans = await planService.getUserPlans();
      setPlans(userPlans);

      if (userPlans.length === 0) {
        setCurrentPlan(null);
        setCurrentMonthPlan(null);
        setAllMonthPlans([]);
        return;
      }

      const existingPlanId = currentPlanIdRef.current;
      const selectedPlanId = existingPlanId && userPlans.some((plan) => plan.id === existingPlanId)
        ? existingPlanId
        : userPlans[0].id;

      if (!selectedPlanId) {
        setCurrentPlan(null);
        setCurrentMonthPlan(null);
        setAllMonthPlans([]);
        return;
      }

      const selectedPlan = await planService.getPlanById(selectedPlanId);
      setCurrentPlan(selectedPlan);
    } catch (err) {
      if (err instanceof Error && err.message === 'Authentication token not available') {
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to load plans');
      console.error('Error loading plans:', err);
    } finally {
      setLoading(false);
    }
  }, [session, resetState]);

  /**
   * Auto-load current month_plan and all month_plans when current plan changes
   */
  useEffect(() => {
    if (currentPlan) {
      loadCurrentMonthPlan(currentPlan.id);
      loadAllMonthPlans(currentPlan.id);
    }
  }, [currentPlan?.id]);

  /**
   * Load all user plans
   */
  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!session) {
      resetState();
      return;
    }

    loadPlans();
  }, [authLoading, session?.user?.id, loadPlans, resetState]);

  /**
   * Create a new plan
   */
  const createPlan = async (data: CreatePlanData): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const newPlan = await planService.createPlan(data);
      
      // Refresh plans list
      await loadPlans();
      
      // Set as current plan
      setCurrentPlan(newPlan);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create plan');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Select a plan
   */
  const selectPlan = async (planId: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const plan = await planService.getPlanById(planId);
      setCurrentPlan(plan);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to select plan');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update plan name
   */
  const updatePlan = async (planId: string, data: UpdatePlanData): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const updatedPlan = await planService.updatePlan(planId, data);
      
      // Update in plans list
      setPlans(prev => prev.map(p => p.id === planId ? { ...p, ...updatedPlan } : p));
      
      // Update current plan if it's the one being updated
      if (currentPlan?.id === planId) {
        setCurrentPlan(updatedPlan);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update plan');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Delete plan
   */
  const deletePlan = async (planId: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      await planService.deletePlan(planId);
      
      // Remove from plans list
      setPlans(prev => prev.filter(p => p.id !== planId));
      
      // Clear current plan if it's the one being deleted
      if (currentPlan?.id === planId) {
        setCurrentPlan(null);
        setCurrentMonthPlan(null);
        
        // Select first available plan
        const remainingPlans = plans.filter(p => p.id !== planId);
        if (remainingPlans.length > 0) {
          await selectPlan(remainingPlans[0].id);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete plan');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refresh plans list
   */
  const refreshPlans = async (): Promise<void> => {
    await loadPlans();
  };

  /**
   * Create month_plan manually with gap filling
   */
  const createMonthPlan = async (
    planId: string,
    data: CreateMonthPlanData
  ): Promise<MonthPlan[]> => {
    try {
      setLoading(true);
      setError(null);
      const createdMonthPlans = await planService.createMonthPlan(planId, data);
      
      // Reload current month plan if we're viewing this plan
      if (currentPlan?.id === planId) {
        await loadCurrentMonthPlan(planId);
        // Also reload all month plans to update the dropdown
        await loadAllMonthPlans(planId);
      }
      
      return createdMonthPlans;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create month plan');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load current month_plan (auto-creates if doesn't exist)
   */
  const loadCurrentMonthPlan = async (planId: string): Promise<void> => {
    try {
      const monthPlan = await planService.getCurrentMonthPlan(planId);
      setCurrentMonthPlan(monthPlan);
      
      // Task 002b: Check and auto-create next month after loading current month
      try {
        await planService.checkAndCreateNextMonth(planId);
      } catch (checkErr) {
        // Non-critical: just log if next month check fails
        console.log('Next month check:', checkErr);
      }
    } catch (err) {
      if (err instanceof Error && err.message === 'Authentication token not available') {
        return;
      }
      console.error('Error loading current month plan:', err);
      setCurrentMonthPlan(null);
    }
  };

  /**
   * Load all month_plans for current plan
   */
  const loadAllMonthPlans = async (planId: string): Promise<void> => {
    try {
      const monthPlans = await planService.getMonthPlans(planId);
      setAllMonthPlans(monthPlans);
    } catch (err) {
      if (err instanceof Error && err.message === 'Authentication token not available') {
        return;
      }
      console.error('Error loading all month plans:', err);
      setAllMonthPlans([]);
    }
  };

  /**
   * Load month_plan by specific date
   */
  const loadMonthPlanByDate = async (monthDate: string): Promise<void> => {
    if (!currentPlan) return;

    try {
      setLoading(true);
      // Check if month_plan exists in cached list
      const existingMonthPlan = allMonthPlans.find(mp => mp.month === monthDate);
      
      if (existingMonthPlan) {
        setCurrentMonthPlan(existingMonthPlan);
      } else {
        // Parse date and create month_plan
        const date = new Date(monthDate);
        const month = date.getMonth() + 1;
        const year = date.getFullYear();
        
        await createMonthPlan(currentPlan.id, { month, year });
        // Reload all month plans
        await loadAllMonthPlans(currentPlan.id);
        
        // Find and set the newly created month_plan
        const newMonthPlans = await planService.getMonthPlans(currentPlan.id);
        const newMonthPlan = newMonthPlans.find(mp => mp.month === monthDate);
        if (newMonthPlan) {
          setCurrentMonthPlan(newMonthPlan);
        }
      }
    } catch (err) {
      if (err instanceof Error && err.message === 'Authentication token not available') {
        setError(null);
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to load month plan');
      console.error('Error loading month plan by date:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Navigate to previous month
   */
  const navigateToPreviousMonth = async (): Promise<void> => {
    if (!currentMonthPlan || !currentPlan) return;

    try {
      // Parse current month (YYYY-MM-DD format)
      const currentDate = new Date(currentMonthPlan.month);
      // Go back one month
      currentDate.setMonth(currentDate.getMonth() - 1);
      
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const monthStr = `${year}-${String(month).padStart(2, '0')}-01`;
      
      await loadMonthPlanByDate(monthStr);
    } catch (err) {
      console.error('Error navigating to previous month:', err);
    }
  };

  /**
   * Navigate to next month
   */
  const navigateToNextMonth = async (): Promise<void> => {
    if (!currentMonthPlan || !currentPlan) return;

    try {
      // Parse current month (YYYY-MM-DD format)
      const currentDate = new Date(currentMonthPlan.month);
      // Go forward one month
      currentDate.setMonth(currentDate.getMonth() + 1);
      
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const monthStr = `${year}-${String(month).padStart(2, '0')}-01`;
      
      await loadMonthPlanByDate(monthStr);
    } catch (err) {
      console.error('Error navigating to next month:', err);
    }
  };

  const value: PlanContextType = {
    plans,
    currentPlan,
    currentMonthPlan,
    allMonthPlans,
    loading,
    error,
    createPlan,
    selectPlan,
    updatePlan,
    deletePlan,
    refreshPlans,
    createMonthPlan,
    loadCurrentMonthPlan,
    navigateToPreviousMonth,
    navigateToNextMonth,
    loadMonthPlanByDate,
  };

  return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
};

/**
 * Hook to use Plan context
 */
export const usePlans = (): PlanContextType => {
  const context = useContext(PlanContext);
  if (context === undefined) {
    throw new Error('usePlans must be used within a PlanProvider');
  }
  return context;
};
